import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { RecordModel } from 'pocketbase';

interface FAQ {
  id: string;
  created: string;
  updated: string;
  question: string;
  answer: string;
  order: number;
  product: string;
  isVisible: boolean;
}

interface FAQRecord extends RecordModel {
  question: string;
  answer: string;
  order: number;
  product: string;
  isVisible: boolean;
}

interface FAQProps {
  productId: string;
}

const FAQItem: React.FC<{ faq: FAQ, isActive: boolean, onClick: () => void }> = ({ 
  faq, 
  isActive, 
  onClick 
}) => {
  return (
    <div className="border-b border-[rgba(0,63,40,0.1)]">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-4 px-4 text-left"
      >
        <span className="text-[rgba(0,63,40,1)] font-medium">{faq.question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-[rgba(0,63,40,1)] transition-transform duration-300 ${
            isActive ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isActive ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div 
          className="px-4 pb-4 text-[rgba(0,63,40,0.8)] prose prose-green"
          dangerouslySetInnerHTML={{ __html: faq.answer }}
        />
      </div>
    </div>
  );
};

export const FAQ: React.FC<FAQProps> = ({ productId }) => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const records = await pb.collection('FAQ').getList<FAQRecord>(1, 50, {
          filter: `product = "${productId}" && isVisible = true`,
          sort: '+order',
        });
        
        // Transform records to match FAQ interface
        const transformedFaqs: FAQ[] = records.items.map(record => ({
          id: record.id,
          created: record.created,
          updated: record.updated,
          question: record.question,
          answer: record.answer,
          order: record.order,
          product: record.product,
          isVisible: record.isVisible
        }));

        setFaqs(transformedFaqs);
      } catch (error) {
        console.error('FAQ fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      loadFaqs();
    }
  }, [productId]);

  if (loading) return null;
  if (faqs.length === 0) return null;

  return (
    <section className="w-full max-w-[500px] mx-auto px-4 py-12">
      <h2 className="text-[rgba(0,63,40,1)] text-4xl font-bold mb-8 text-center">
        Are you Wondering?
      </h2>
      <div className="bg-white">
        {faqs.map((faq) => (
          <FAQItem
            key={faq.id}
            faq={faq}
            isActive={activeId === faq.id}
            onClick={() => setActiveId(activeId === faq.id ? null : faq.id)}
          />
        ))}
      </div>
    </section>
  );
};